import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { trackEvent } from "@faire/web/common/trackEvent";
import { AccordionItem } from "@faire/web/slate/Accordion";
import { Button } from "@faire/web/slate/Button";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import brands from "@faire/web-api--source/routes/www/brands";
import c1Link from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import brandValueCategorySubCategoryLink from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/brand-value/brandValue";
import c3Link from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/c3CategoryUrlId";
import faireMarkets from "@faire/web-api--source/routes/www/markets";
import supportArticle from "@faire/web-api--source/routes/www/support/articles/articleId";
import brandSupport from "@faire/web-api--source/routes/www/support/brands";
import retailerSupport from "@faire/web-api--source/routes/www/support/retailers";
import { History } from "history";
import React from "react";

export const trackFaqClick = (index: number) => {
  trackEvent(`logged-out-category-page/apparel-faq/question-${index}/click`);
};

export const LOGGED_OUT_CATEGORY_PAGE_APPAREL_FAQS = (
  history: History
): AccordionItem[] => [
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="What is a wholesale apparel brand?"
          description={{
            img: "https://cdn.faire.com/fastly/a8697d75a01aec84cdb657588b92ac5fe63ab5866442fc961f6aa7925b5ab059.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="Wholesale clothing brands are brands looking to sell their products
        directly to resellers, often smaller outlets and retailers. Wholesale
        brands either market to resellers and retailers themselves, or list
        their products on platforms such as Faire, where they can be connected
        with a wide range of retail businesses looking to buy apparel wholesale.
       {lineBreak}
       {lineBreak}
        Wholesale clothing brands are often made by independent designers who
        historically operate in local markets and are looking for ways to expand
        their brand awareness and audience size. This means that businesses
        buying wholesale get access to some of the best emerging clothing makers
        today before they strike it big."
          description={{
            img: "https://cdn.faire.com/fastly/a8697d75a01aec84cdb657588b92ac5fe63ab5866442fc961f6aa7925b5ab059.png",
          }}
          values={{
            lineBreak: <br />,
          }}
        />
      </Typography>
    ),
  },
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="How do I find wholesale clothing brands?"
          description={{
            img: "https://cdn.faire.com/fastly/7c182abc6ae38f29e694c8411b14ecc138d93df44db9bafd03362a52a384953c.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="Finding wholesale clothing brands is no simple task. While there may be
        some in your area that you have connections with, it might be difficult
        to continuously search through the internet to find wholesale clothing
        brands that match a retailer’s style.
        {lineBreak}
        {lineBreak}
        That’s where Faire comes in. Faire’s platform consolidates <LinkFormatting>thousands of different wholesale clothing brands</LinkFormatting> onto a single site, making it possible to buy from independent garment
        makers you might otherwise have never heard of. Just a quick browse
        through Faire’s apparel listings can offer you hundreds of brands across
        a range of styles that are perfect for your store."
          description={{
            img: "https://cdn.faire.com/fastly/7c182abc6ae38f29e694c8411b14ecc138d93df44db9bafd03362a52a384953c.png",
          }}
          values={{
            LinkFormatting: (chunks) => (
              <Button
                variant="tertiary"
                linkTo={c3Link("Women", "Apparel", "New Arrivals")}
              >
                {chunks}
              </Button>
            ),
            lineBreak: <br />,
          }}
        />
      </Typography>
    ),
  },
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="What does the wholesale apparel process look like?"
          description={{
            img: "https://cdn.faire.com/fastly/2a71bdc8b66f67d3d37fce6c3b649457d68f0a4e7c2e90a0624bd02a7c24cbeb.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="Buying wholesale apparel is like buying anything from a typical e-commerce website – you can browse apparel brands and stock your store within a few clicks! Faire also offers Net 60 payment terms, meaning that you won’t be responsible to pay for some of the products until 60 days after placing your order. This gives you a couple of months to resell what you purchase, improving your cash flow."
          description={{
            img: "https://cdn.faire.com/fastly/2a71bdc8b66f67d3d37fce6c3b649457d68f0a4e7c2e90a0624bd02a7c24cbeb.png",
          }}
        />
      </Typography>
    ),
  },
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="What are women's wholesale apparel options?"
          description={{
            img: "https://cdn.faire.com/fastly/4ce2a8b512e96f08f7f70eaa993cded3c16fdcb43a1dc815c8ab314edebd307f.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="The options offered through Faire include affordable, designer-quality
          garments. When viewing Faire’s women’s apparel page, you can find a
          number of different brands offering jeans, leggings, blouses, dresses,
          printed t-shirts, and much, much more.
          {lineBreak}
          {lineBreak}
          Beyond clothing, we also showcase brands offering adjacent products that
          retailers may be interested in, such as jewelry, handbags, accessories,
          beauty products, and stylish home decor. No matter the look you’re going
          for, no matter the target audience, there’s a wholesale brand calling
          your name."
          description={{
            img: "https://cdn.faire.com/fastly/4ce2a8b512e96f08f7f70eaa993cded3c16fdcb43a1dc815c8ab314edebd307f.png",
          }}
          values={{
            lineBreak: <br />,
          }}
        />
      </Typography>
    ),
  },
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="What is the benefit of buying wholesale clothing on Faire?"
          description={{
            img: "https://cdn.faire.com/fastly/2daa0c3af02f370ca7dd52566ea4adb7a1fa29713b55b309c81f828cc3547657.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="Most of the wholesalers currently selling on Faire are small businesses,
          artisans, or independent garment makers. This means that retailers
          buying through Faire have access to a range of high-quality products
          that may not be widely available in their area already. Faire’s large
          selection of wholesalers also ensures that thousands of popular and
          differentiated products are available on the site, such as <PlusSizeLinkFormatting>plus size</PlusSizeLinkFormatting> clothing.
          {lineBreak}
          {lineBreak}
          Buying wholesale on Faire also makes it easier for retailers to stay on
          top of current trends. The moment a <NewBrandLinkFormatting>new brand</NewBrandLinkFormatting> is ready to sell through Faire, its products are available for you to
          purchase. Within weeks of a new garment becoming available for purchase,
          it can be on your shelves. Retailers can also benefit from Faire’s Net
          60 payment terms and ability to make a return on their first order."
          description={{
            img: "https://cdn.faire.com/fastly/2daa0c3af02f370ca7dd52566ea4adb7a1fa29713b55b309c81f828cc3547657.png",
          }}
          values={{
            PlusSizeLinkFormatting: (chunks) => (
              <Button variant="tertiary" linkTo={c1Link("Extended Sizes")}>
                {chunks}
              </Button>
            ),
            NewBrandLinkFormatting: (chunks) => (
              <Button
                variant="tertiary"
                linkTo={c3Link("Women", "Apparel", "New Arrivals")}
              >
                {chunks}
              </Button>
            ),
            lineBreak: <br />,
          }}
        />
      </Typography>
    ),
  },
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="Can I preorder wholesale clothing through Faire?"
          description={{
            img: "https://cdn.faire.com/fastly/2ed18c9cb1950ad139b96d8307844cf87fffd529263c646e3fe638f1de7b6083.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="Yes! We offer our users the option to preorder to help you strategically
          plan your buying. Check out <LinkFormatting>our preorder guide</LinkFormatting> to learn more!"
          values={{
            LinkFormatting: (chunks) => (
              <Button
                variant="tertiary"
                linkTo={supportArticle("360038557671")}
              >
                {chunks}
              </Button>
            ),
          }}
          description={{
            img: "https://cdn.faire.com/fastly/2ed18c9cb1950ad139b96d8307844cf87fffd529263c646e3fe638f1de7b6083.png",
          }}
        />
      </Typography>
    ),
  },
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="Does Faire host any wholesale apparel events?"
          description={{
            img: "https://cdn.faire.com/fastly/767b29af7f77b683fd80c830d2151e2ffeb25f1cd1b93d17a7bf47f020618272.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="Faire hosts a virtual trade show every summer and winter where wholesale
          brands can showcase their new and exciting styles, at alluring discount
          prices. <FaireMarketLinkFormatting>Faire’s markets</FaireMarketLinkFormatting> also feature educational livestreams and insights from industry experts,
          making them a great way to stay up-to-date without having to leave your
          home."
          values={{
            FaireMarketLinkFormatting: (chunks) => (
              <Button variant="tertiary" linkTo={faireMarkets()}>
                {chunks}
              </Button>
            ),
          }}
          description={{
            img: "https://cdn.faire.com/fastly/767b29af7f77b683fd80c830d2151e2ffeb25f1cd1b93d17a7bf47f020618272.png",
          }}
        />
      </Typography>
    ),
  },
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="How does Faire benefit women's wholesale apparel brands?"
          description={{
            img: "https://cdn.faire.com/fastly/8e71aeda67d19ab1faa819ccb9277c6e31cf8e2b49ac62562b89ad00284d0e5c.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="There are thousands of apparel brands on the market creating excellent
          products, ethically and with <SustainabilityLinkFormatting>sustainability in mind</SustainabilityLinkFormatting>. Many of these brands might not be able to afford the outreach and
          promotional costs necessary to market to retailers on their own, so
          Faire steps in to help.
          {lineBreak}
          {lineBreak}
          Every single day, dozens of retailers purchase from independent women’s
          apparel wholesalers through Faire’s platform. Brands on Faire’s platform
          are carefully vetted -- their products are high quality and ready to
          become the next best seller. Want to know more? Check out our <BrandGuideLinkFormatting>guides for brands</BrandGuideLinkFormatting>."
          values={{
            SustainabilityLinkFormatting: (chunks) => (
              <Button
                variant="tertiary"
                linkTo={brandValueCategorySubCategoryLink(
                  "Women",
                  "Apparel",
                  "eco-friendly"
                )}
              >
                {chunks}
              </Button>
            ),
            BrandGuideLinkFormatting: (chunks) => (
              <Button variant="tertiary" linkTo={brandSupport()}>
                {chunks}
              </Button>
            ),
            lineBreak: <br />,
          }}
          description={{
            img: "https://cdn.faire.com/fastly/8e71aeda67d19ab1faa819ccb9277c6e31cf8e2b49ac62562b89ad00284d0e5c.png",
          }}
        />
      </Typography>
    ),
  },
  {
    question: (
      <Typography variant="subheadingSansRegular">
        <StrictLocalMsg
          defaultMessage="How does Faire benefit women's apparel retailers?"
          description={{
            img: "https://cdn.faire.com/fastly/f87a759e4a1ff1a9a9741fc9ab6dac6ae6c224f5e50d9041703c54ad9b5e3b3d.png",
          }}
        />
      </Typography>
    ),
    answer: (
      <Typography>
        <StrictLocalMsg
          defaultMessage="Retailers make a profit by securing wholesale-priced products, but
          finding their next best seller can be time-consuming and difficult.
          Through Faire, retailers have access to thousands of quality wholesale
          brands across category, style and price point. Retailers also have the
          benefit of stocking their shelves within a few clicks, Net 60 payment
          terms, and the ability to make a return on their first order if
          something doesn’t work out. This wide access allows retailers to
          showcase new and exciting products, and draw in new customers to their
          stores. What’s not to like? If you want to know more, check out our <RetailerGuideLinkFormatting>guides for retailers</RetailerGuideLinkFormatting>.
          {lineBreak}
          {lineBreak}
          Getting started with Faire is easy: just look for the products you want,
          and we’ll handle the rest. If you are a retailer in need of stock, we’re
          here to help <BestSellingLinkFormatting>find your next best selling garments</BestSellingLinkFormatting>. If you’re a brand wanting to sell your products, <GetStartedLinkFormatting>getting started is just a click away</GetStartedLinkFormatting>."
          values={{
            RetailerGuideLinkFormatting: (chunks) => (
              <Button variant="tertiary" linkTo={retailerSupport()}>
                {chunks}
              </Button>
            ),
            BestSellingLinkFormatting: (chunks) => (
              <Button
                variant="tertiary"
                onClick={() =>
                  popSignUp(history, SignUpType.GENERIC, () => null, {
                    element: SignUpElement.BELOW_FOLD,
                    sourceId: "apparel_faq_question_womens_apparel",
                  })
                }
              >
                {chunks}
              </Button>
            ),
            GetStartedLinkFormatting: (chunks) => (
              <Button variant="tertiary" linkTo={brands()}>
                {chunks}
              </Button>
            ),
            lineBreak: <br />,
          }}
          description={{
            img: "https://cdn.faire.com/fastly/f87a759e4a1ff1a9a9741fc9ab6dac6ae6c224f5e50d9041703c54ad9b5e3b3d.png",
          }}
        />
      </Typography>
    ),
  },
];
